import {
  Component,
  Inject,
  OnInit,
  ChangeDetectorRef,
  NgZone,
} from "@angular/core";
import { MAT_DIALOG_DATA, MatDialogRef } from "@angular/material/dialog";
import { DataPatientCaseWorkItemEventModel } from "../../../../../models/accountsReceivable/data-patient-case-work-item-event";
import { DoctorByTDOCIDModel } from "../../../../ar/models/doctor-by-tdocid.model";
import { EnumValidValueItemModel } from "../../../../../models/enum/enum-valid-value-item.model";
import { StaticDataService } from "../../../../../services/static-data.service";
import { CaseFormsService } from "../../../case-forms.service";
import { DataPatientCaseWorkItemModel } from "src/app/models/accountsReceivable/data-patient-case-work-item.model";
import { Globals } from "src/app/globals";

@Component({
  selector: "app-case-view-work-item-events-dialog",
  templateUrl: "./case-view-work-item-events-dialog.component.html",
  styleUrls: ["./case-view-work-item-events-dialog.component.scss"],
})
export class CaseViewWorkItemEventsDialogComponent implements OnInit {
  showDetailedView: boolean = false; // Toggle for detailed view
  events: DataPatientCaseWorkItemEventModel[] = []; // Array to store events
  doctors: DoctorByTDOCIDModel[]; // List of doctors
  workItem: DataPatientCaseWorkItemModel; // Current work item
  workItemEventTypes: EnumValidValueItemModel[] = []; // List of event types
  selectedProcedure: DataPatientCaseWorkItemModel | null = null; // Selected procedure

  constructor(
    private dialogRef: MatDialogRef<CaseViewWorkItemEventsDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: any,
    private staticDataService: StaticDataService,
    private caseService: CaseFormsService,
    private changeDetectorRef: ChangeDetectorRef,
    private ngZone: NgZone,
    public globals: Globals
  ) {
    this.doctors = data.doctors; // Assign doctors from injected data
    this.workItem = data.workItem as DataPatientCaseWorkItemModel; // Assign the work item from injected data
    this.selectedProcedure = data.selectedProcedure || null; // Assign the selected procedure or set to null
  }

  ngOnInit(): void {
    this.loadEnums(); // Load available event types
    this.loadBasicEvents(); // Load events for basic view by default
  }

  // Load event types from static data service
  loadEnums(): void {
    this.workItemEventTypes =
      this.staticDataService.getValidValueItemsByValidValueName(
        "enumWorkItemEventType"
      );
  }

  // Load events for the basic view (only for the selected work item)
  loadBasicEvents(): void {
    this.caseService.getEventsForWorkItem(this.workItem).subscribe(
      (res: DataPatientCaseWorkItemEventModel[]) => {
        this.ngZone.run(() => {
          this.events = res; // Assign fetched events to the local array
          this.changeDetectorRef.detectChanges(); // Trigger change detection
        });
      },
      (error) => {
        console.error("Error loading events for work item:", error); // Log error if event loading fails
      }
    );
  }

  // Load all events for the detailed view (all events related to the case)
  loadDetailedEvents(): void {
    this.caseService.getEventsForCase(this.workItem).subscribe(
      (res: DataPatientCaseWorkItemEventModel[]) => {
        console.log("case event data: ", res);
        this.ngZone.run(() => {
          this.events = res; // Assign all fetched events to the local array
          this.changeDetectorRef.detectChanges(); // Trigger change detection
        });
      },
      (error) => {
        console.error("Error loading events for case:", error); // Log error if event loading fails
      }
    );
  }

  // Toggle between basic and detailed view
  toggleDetailedView(): void {
    this.showDetailedView = !this.showDetailedView;

    if (this.showDetailedView) {
      this.loadDetailedEvents(); // Load all events for the detailed view
    } else {
      this.loadBasicEvents(); // Load events for the basic view
    }

    this.changeDetectorRef.detectChanges(); // Trigger change detection
  }

  // Format duration in hours, minutes, and seconds
  formatDuration(duration: number): string {
    const hours = Math.floor(duration / 60);
    const minutes = Math.floor(duration % 60);
    const seconds = Math.floor((duration * 60) % 60);
    if (hours > 0) {
      return `${hours} hr ${minutes} min`;
    } else if (minutes > 0) {
      return `${minutes} min ${seconds} sec`;
    } else {
      return `${seconds} sec`;
    }
  }

  // Get the header text based on the current view
  getHeaderText(): string {
    if (this.showDetailedView) {
      const toothList = this.returnToothList(
        this.workItem?.dataPatientCaseItemTeeth || []
      );
      return `Case: ${this.globals.currentPatientSelectedCaseItem.Description} ${this.globals.currentPatientSelectedCaseItem.DateString} - Events`;
    } else {
      return `${this.getProcedureDescription()} - Events`;
    }
  }

  // Generate a string representing the list of teeth
  returnToothList(teeth: any[]): string {
    if (teeth.length === 0) return "N/A";
    return teeth.map((tooth) => tooth.name || tooth.ToothID).join(", ");
  }

  // Get the description of the selected procedure
  getProcedureDescription(): string {
    return this.selectedProcedure ? this.selectedProcedure.Description : "N/A";
  }

  // Close the dialog
  onOkClick(): void {
    this.dialogRef.close();
  }

  // Get the display value of the event type based on its ID
  getEventType(enumWorkItemEventTypeID: string): string {
    const enu = this.workItemEventTypes.find(
      (p) => p.ValidValueItemId === enumWorkItemEventTypeID
    );
    return enu ? enu.ItemDisplayValue : "";
  }

// Format date strings into a readable format
getDate(d: string): string {
  const dt = new Date(d); // Convert to local time automatically
  return isNaN(dt.getTime()) ? 'Invalid Date' : dt.toLocaleString(); // Check for invalid date
}

  // Get the name of the doctor based on the provided ID
  getDoctorName(ProviderDoctorId: string): string {
    const doctor = this.doctors.find(
      (p) => p.DoctorId?.toLowerCase() === ProviderDoctorId?.toLowerCase()
    );
    return doctor ? "Dr. " + doctor.LastName : "";
  }
}
